import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { getErrorMessage } from "@/lib/errors";

const DATE_FIELDS = {
  indent: ["indent_date", "Indent date"],
  requisition: ["requisition_date", "Requisition date"],
  rfq: ["rfq_date", "RFQ date"],
  purchaseOrder: ["po_date", "PO date"],
};

const ROUTES = {
  indent: "indents",
  requisition: "requisitions",
  rfq: "rfqs",
  purchaseOrder: "purchase-orders",
};

const blankItem = (phase) => phase === "purchaseOrder"
  ? { description: "", quantity: "", rate: "", is_capital: false }
  : { description: "", quantity: "", estimated_rate: "" };

export function DraftProcurementEditor({ phase, document, projects = [], onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dateField, dateLabel] = DATE_FIELDS[phase];
  const isPO = phase === "purchaseOrder";

  useEffect(() => {
    if (!document) return;
    setForm({
      project_id: document.project_id || "",
      [dateField]: document[dateField] || "",
      due_date: document.due_date || "",
      remarks: document.remarks || "",
    });
    setItems((document.items || []).map((item) => ({
      ...item,
      quantity: String(item.quantity ?? ""),
      estimated_rate: item.estimated_rate == null ? "" : String(item.estimated_rate),
      rate: item.rate == null ? "" : String(item.rate),
    })));
  }, [document, dateField]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateItem = (index, field, value) => setItems((current) => current.map((item, i) => i === index ? { ...item, [field]: value } : item));
  const pickItem = (index, picked) => setItems((current) => current.map((item, i) => i === index
    ? { ...item, item_id: picked.id, description: picked.name, uom_id: picked.uom_id || item.uom_id || null,
      ...(isPO
        ? { rate: Number(picked.standard_rate) > 0 ? String(picked.standard_rate) : item.rate }
        : { estimated_rate: Number(picked.standard_rate) > 0 ? String(picked.standard_rate) : item.estimated_rate }) }
    : item));

  const submit = async (event) => {
    event.preventDefault();
    if (!document || !items.length) return;
    setSaving(true);
    try {
      const payload = {
        company_id: document.company_id,
        project_id: form.project_id || null,
        [dateField]: form[dateField],
        remarks: form.remarks || null,
        items: items.map((item) => ({
          item_id: item.item_id || null,
          description: item.description,
          uom_id: item.uom_id || null,
          quantity: item.quantity,
          wbs_item_id: item.wbs_item_id || null,
          ...(isPO
            ? { rate: item.rate, is_capital: Boolean(item.is_capital) }
            : { estimated_rate: item.estimated_rate || null }),
        })),
      };
      if (phase === "rfq") {
        payload.due_date = form.due_date || null;
        payload.vendor_ids = [];
      }
      if (isPO) {
        payload.branch_id = document.branch_id || null;
        payload.vendor_id = document.vendor_id;
        payload.tax_code_id = document.tax_code_id || null;
        payload.rfq_id = document.rfq_id || null;
      }
      await api.put(`/procurement/${ROUTES[phase]}/${document.id}`, payload);
      toast.success(`${document.number} updated`);
      onClose();
      onSaved?.();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!document} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid={`edit-${phase}-dialog`}>
        <DialogHeader><DialogTitle>Edit draft {document?.number}</DialogTitle></DialogHeader>
        {document && (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{dateLabel}</Label>
                <Input required type="date" value={form[dateField] || ""} onChange={(event) => updateForm(dateField, event.target.value)} />
              </div>
              <div>
                <Label>Project</Label>
                <Select value={form.project_id || "none"} onValueChange={(value) => updateForm("project_id", value === "none" ? "" : value)}>
                  <SelectTrigger><SelectValue placeholder="Not linked" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked</SelectItem>
                    {projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.code} — {project.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {phase === "rfq" && (
                <div>
                  <Label>Due date</Label>
                  <Input type="date" value={form.due_date || ""} onChange={(event) => updateForm("due_date", event.target.value)} />
                </div>
              )}
              <div className="sm:col-span-2">
                <Label>Remarks</Label>
                <Input value={form.remarks || ""} onChange={(event) => updateForm("remarks", event.target.value)} />
              </div>
            </div>
            {isPO && <p className="text-xs text-muted-foreground">Company and vendor are fixed for this draft. Existing RFQ and requisition links are retained.</p>}
            {phase === "rfq" && <p className="text-xs text-muted-foreground">Current vendor invitations and quotes are retained.</p>}
            <section className="space-y-2">
              <Label>Items</Label>
              {items.map((item, index) => (
                <div key={item.id || index} className="space-y-2 rounded-lg border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-[180px] flex-1">
                      <ItemPicker required value={item.description} onChange={(value) => updateItem(index, "description", value)} onPick={(picked) => pickItem(index, picked)} />
                    </div>
                    <Input required type="number" min="0.001" step="0.001" placeholder="Qty" className="w-24" value={item.quantity} onChange={(event) => updateItem(index, "quantity", event.target.value)} />
                    <Input type="number" min="0" step="0.01" placeholder={isPO ? "Rate" : "Est. rate"} className="w-28" value={isPO ? item.rate : item.estimated_rate} onChange={(event) => updateItem(index, isPO ? "rate" : "estimated_rate", event.target.value)} />
                    {isPO && (
                      <label className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Checkbox checked={Boolean(item.is_capital)} onCheckedChange={(checked) => updateItem(index, "is_capital", Boolean(checked))} /> Asset
                      </label>
                    )}
                    {items.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => setItems((current) => current.filter((_, i) => i !== index))} aria-label="Remove item"><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                  </div>
                  <WbsItemSelect projectId={form.project_id} value={item.wbs_item_id || ""} onChange={(value) => updateItem(index, "wbs_item_id", value)} />
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={() => setItems((current) => [...current, blankItem(phase)])}><Plus className="mr-1 h-3.5 w-3.5" /> Add item</Button>
            </section>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={saving || !items.length}>{saving ? "Saving…" : "Save changes"}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
